import { useState } from "react";

import { message } from "antd";
import axios, { AxiosRequestConfig } from "axios";

import { API_URL, getLocalStorageCurrency, LOCALSTORAGE_KEY } from "@config";
import { HTTPServerError } from "@lib/error";
import { Status } from "@models/common";
import report from "@report";
import { PerfResourceTiming } from "@utils/performace";
import { initDeviceId, initPageLanguage } from "@utils/util";
import { appendUrlParams } from "@utils/url";
import { isInIframe } from "@utils/window";

import { handleLoginStatus1302, handleStatus411 } from "./hooks";
import { searchApi } from "./searchApi";
import commonservice from "./commonservice";
import { resolveTraceIdFromHeaders } from "./http/traceId";

axios.defaults.baseURL = API_URL;
axios.defaults.timeout = 60000;
if (isInIframe()) {
  axios.defaults.withCredentials = true;
}

// 刷新 token 的锁：存储正在进行的 refresh Promise
let refreshingPromise: Promise<boolean | null> | null = null;

const autoRefreshToken = async (): Promise<boolean | null> => {
  // 如果已经有 refresh 在进行，直接返回同一个 Promise
  if (refreshingPromise) {
    return refreshingPromise;
  }

  refreshingPromise = (async () => {
    try {
      const { accessToken }: any = await commonservice.refreshToken();
      if (accessToken) {
        localStorage.setItem(LOCALSTORAGE_KEY.ACCESS_TOKEN, accessToken);
        return true;
      }
      return null;
    } catch (error) {
      redirectToLogin();
      return null;
    } finally {
      refreshingPromise = null;
    }
  })();

  return refreshingPromise;
};

const redirectToLogin = () => {
  if (["/login", "/signup", "/authorization", "/privacy-policy"].includes(window.location.pathname)) return;
  const needSignUp = !localStorage.getItem("userName");
  const redirectUrl = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = needSignUp ? `/signup?redirectUrl=${redirectUrl}` : `/login?redirectUrl=${redirectUrl}`;
};

// 获取通用请求头（包含 Authorization）
const getCommonHeaders = () => {
  const language = localStorage.getItem(LOCALSTORAGE_KEY.LANGUAGE) || initPageLanguage();
  const region = localStorage.getItem(LOCALSTORAGE_KEY.REGION) || "US";
  const currency = getLocalStorageCurrency();
  const accessToken = localStorage.getItem(LOCALSTORAGE_KEY.ACCESS_TOKEN);

  const headers: Record<string, string> = {
    language,
    country: region,
    currency,
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return headers;
};

interface Response<ResponseData> {
  code: any;
  message: string;
  success: boolean;
  data: ResponseData;
}

function reportFn(type: string, url: string | number, value: string | number) {
  if (url !== "/api/log") {
    report.event("searchListApi", {
      kalo_pos: "search_list_" + type,
      kalo_type: url,
      kalo_value: value,
    });
  }
}

export const reportProductParseInfoByLinkError = (payload: Record<string, any>) => {
  report.event("productParseInfoByLinkError", {
    kalo_pos: "product_parse_info_by_link_error",
    kalo_value: payload,
  });
};

const isAxiosTimeout = (error: unknown) => {
  if (!axios.isAxiosError(error)) return false;
  if (error.code === "ECONNABORTED") return true;
  return error.message.toLowerCase().includes("timeout");
};

const reportTimeout = (
  error: unknown,
  apiUrl: string,
  axiosParams: AxiosRequestConfig,
  requestTime: number,
): boolean => {
  if (!isAxiosTimeout(error) || !axios.isAxiosError(error)) return false;
  report.event("apiTimeout", {
    kalo_pos: "api_timeout",
    kalo_type: apiUrl,
    kalo_json: {
      timeoutMs: axiosParams.timeout ?? axios.defaults.timeout,
      elapsedMs: Date.now() - requestTime,
      axiosCode: error.code || "",
      method: axiosParams.method || "",
    },
  });
  return true;
};

const request = <ResponseData>(axiosParams: AxiosRequestConfig, extra?: { cache: string }): Promise<ResponseData> => {
  let requestTime: number;
  let responseTime: number;
  let apiUrl = axiosParams.url || "";

  // 自动添加 bizCode、tc 到 URL
  const params = new URLSearchParams(window.location.search);
  const savedParams = JSON.parse(sessionStorage.getItem("auth_saved_params") || "{}");
  const bizCode = params.get("bizCode") || savedParams.bizCode;
  const tc = params.get("tc") || savedParams.tc;

  const newApiUrl = appendUrlParams(apiUrl, [
    { key: "bizCode", value: bizCode },
    { key: "tc", value: tc },
  ]);
  if (newApiUrl !== apiUrl) {
    apiUrl = newApiUrl;
    axiosParams = { ...axiosParams, url: apiUrl };
  }

  return new Promise(async (resolve, reject) => {
    await initDeviceId();
    const perfResourceTiming = new PerfResourceTiming(apiUrl, axiosParams);
    const headers = getCommonHeaders();
    requestTime = new Date().getTime();
    if (extra && extra.cache) {
      const key = extra.cache + "." + headers.language + "." + headers.currency;
      try {
        const str = localStorage.getItem(key);
        if (str) {
          let value = JSON.parse(str);
          if (value) {
            resolve(value);
          }
        }
      } catch (error) {}
    }

    try {
      let response = await axios.request<Response<ResponseData>>({
        ...axiosParams,
        headers,
      });
      if (
        response.status === 200 &&
        response.headers["content-type"]?.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
      ) {
        perfResourceTiming.reportSuccess(response);
        // @ts-ignore
        return resolve(response);
      }
      if (response.status === 200 && response.data.success) {
        perfResourceTiming.reportSuccess(response);
        if (extra && extra.cache) {
          const key = extra.cache + "." + headers.language + "." + headers.currency;
          try {
            localStorage.setItem(key, JSON.stringify(response.data.data));
          } catch (error) {}
        }
        responseTime = new Date().getTime();
        if (axiosParams.url && searchApi.includes(axiosParams.url)) {
          reportFn("success", apiUrl, responseTime - requestTime);
        }
        resolve(response.data.data);
      } else {
        perfResourceTiming.reportFailed(response);
        responseTime = new Date().getTime();
        if (axiosParams.url && searchApi.includes(axiosParams.url)) {
          reportFn("error", apiUrl, response.data.message);
        }
        //兼容一下根据不同code处理不同错误
        if (response.data.code == 1302) {
          handleLoginStatus1302();
        }
        if (response.data.code) {
          reject({
            code: response.data.code,
            message: response.data.message,
          });
          return;
        }
        reject(response.data.message || "server error");
      }
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        // ajax鉴权失败跳转登录
        if (error.response?.status === 401) {
          if (isInIframe()) {
            window.parent.postMessage({ type: "LOGOUT" }, "*");
            return;
          }
          if ((error.response?.data as any)?.code === "TOKEN_EXPIRED") {
            const isSuccess = await autoRefreshToken();
            if (isSuccess) {
              // 刷新成功，重试原请求
              try {
                const retryResponse = await axios.request<Response<ResponseData>>({
                  ...axiosParams,
                  headers: getCommonHeaders(),
                });
                if (retryResponse.status === 200 && retryResponse.data.success) {
                  resolve(retryResponse.data.data);
                  return;
                }
              } catch (retryError) {
                console.error("Retry request failed:", retryError);
              }
            }
          } else {
            redirectToLogin();
            reject(error);
          }
        }
        try {
          if (error.response?.status === 410) {
            message.config({ maxCount: 1 });
            message.warning(
              (error.response?.data as any)?.message ||
                "We have detected that your account had highly suspected behaviors. For your account safety, we have locked your account.",
            );
          }
        } catch (error) {
          console.error(error);
        }
        if (error.response?.status === 411) {
          handleStatus411();
        }
        const isTimeoutError = reportTimeout(error, apiUrl, axiosParams, requestTime);
        if (apiUrl === "/api/product/parse-info-by-link") {
          reportProductParseInfoByLinkError(axiosParams.data || {});
        } else if (!isTimeoutError) {
          reportFn("error", apiUrl, error.message);
        }
        reject(
          new HTTPServerError((error?.response?.data as any)?.message || error.message, parseTraceID(error?.response)),
        );
        perfResourceTiming.reportFailed(error?.response, error?.message);
      } else {
        reject(new Error("unexpect error"));
        reportFn("error", apiUrl, "unexpect error");
        // @ts-ignore
        perfResourceTiming.reportFailed(error?.response, error?.message);
      }
    }
  });
};

// @ts-ignore
const handleError = (reject, perfResourceTiming, response, message) => {
  perfResourceTiming.reportFailed(response);
  const traceID = parseTraceID(response) || "";
  reject({
    code: response?.data?.code || "",
    message: message || "",
    traceID,
    // 与新版 HTTP client 命名保持一致，便于上层统一读取
    traceId: traceID,
  });
};

const requestV2 = <ResponseData>(axiosParams: AxiosRequestConfig): Promise<ResponseData> => {
  const apiUrl = axiosParams.url || "";
  const requestTime = Date.now();

  return new Promise(async (resolve, reject) => {
    await initDeviceId();
    const perfResourceTiming = new PerfResourceTiming(apiUrl, axiosParams);
    const headers = getCommonHeaders();

    try {
      let response = await axios.request<Response<ResponseData>>({
        ...axiosParams,
        headers,
      });
      if (response.status === 200 && response.data.success) {
        perfResourceTiming.reportSuccess(response);
        resolve(response.data.data);
      } else {
        handleError(reject, perfResourceTiming, response, response.data.message);
      }
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        // ajax鉴权失败跳转登录
        if (error.response?.status === 401) {
          if (isInIframe()) {
            window.parent.postMessage({ type: "LOGOUT" }, "*");
            return;
          }
          if ((error.response?.data as any)?.code === "TOKEN_EXPIRED") {
            const isSuccess = await autoRefreshToken();
            if (isSuccess) {
              // 刷新成功，重试原请求
              try {
                const retryResponse = await axios.request<Response<ResponseData>>({
                  ...axiosParams,
                  headers: getCommonHeaders(),
                });
                if (retryResponse.status === 200 && retryResponse.data.success) {
                  resolve(retryResponse.data.data);
                  return;
                }
              } catch (retryError) {
                console.error("Retry request failed:", retryError);
              }
            }
          } else {
            redirectToLogin();
            return;
          }
        }
        try {
          if (error.response?.status === 410) {
            message.config({ maxCount: 1 });
            message.warning(
              (error.response?.data as any)?.message ||
                "We have detected that your account had highly suspected behaviors. For your account safety, we have locked your account.",
            );
          }
        } catch (error) {
          console.error(error);
        }
        if (error.response?.status === 411) {
          handleStatus411();
        }
        reportTimeout(error, apiUrl, axiosParams, requestTime);
        handleError(reject, perfResourceTiming, error?.response, error?.message);
      } else {
        handleError(reject, perfResourceTiming, (error as any)?.response, (error as any)?.message);
      }
    }
  });
};

const URLS = {
  FETCH_AREA: { method: "get", url: "/api/configs" },
};

function parseTraceID(response: any): string {
  // 复用新版 HTTP client 的解析规则：优先 x-trace-id，其次从 x-amzn-trace-id 中正则提取 Root=...
  return resolveTraceIdFromHeaders(response?.headers as Record<string, string> | undefined);
}

export const useRequest = <Req = any, Res = any>(service: (reqData: Req) => Promise<Res>) => {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<unknown>(null);
  const [data, setData] = useState<Res | null>(null);

  const request = async (reqData: Req) => {
    try {
      setStatus("loading");
      const rspData = await service(reqData);

      setData(rspData);
      setStatus("succeeded");
    } catch (err) {
      setError(err);
      setStatus("failed");
    }
  };
  return { status, error, data, request };
};
export { request, requestV2, URLS };
